import React from 'react'
import {
  Form,
  Input,
  Switch,
  Button,
  Divider,
  message,
  PageHeader,
  Row,
  Col,
  Tabs
} from 'antd'
import { useMutation, useApolloClient } from '@apollo/client'
import { UPDATE_SETTINGS } from './actions'
import UpdateNotice from '../Utils/UpdateNotice'
import { LocationSettingProps } from './Settings'
import QRCode from 'react-qr-code'
import { useSession, isAdmin, isManager } from '@slerp/accounts'
import { PageTooltip } from './../Widgets'
import { onBlurScrollNumber, onFocusScrollNumber } from 'components/Utils/price'

const { useForm } = Form
const { TabPane } = Tabs

const TableSettings = (props: LocationSettingProps) => {
  const { merchant, store } = props
  const [form] = useForm()
  const [updateSetting, { loading, error }] = useMutation(UPDATE_SETTINGS, {
    fetchPolicy: 'no-cache'
  })
  const client = useApolloClient()

  const { merchant: merchantSession, user } = useSession()
  const canManage = isAdmin(user) || isManager(user)
  const tableUrl = store.settings.table_enabled
    ? `https://${merchantSession.slug}.slerp.com/order/table/${store.slug}`
    : undefined

  const onFinish = (values: any) => {
    const {
      table_enabled,
      table_welcome_message,
      table_count,
      table_counter_pickup,
      table_counter_pickup_message,
      table_choices
    } = values

    if (form.validateFields()) {
      message.loading('Updating... Please wait.')
      updateSetting({
        variables: {
          merchant_id: merchant.id,
          id: store.id,
          settings: {
            table_enabled,
            table_welcome_message,
            table_count,
            table_counter_pickup,
            table_counter_pickup_message,
            table_choices
          }
        }
      }).then((result) => {
        message.destroy()
        client.resetStore()
        message.success('Order at Table Settings Updated', 1)
      })
    } else {
      message.destroy()
      message.error('Unable to update the table settings. Please try again.')
    }
  }

  return (
    <>
      <Tabs defaultActiveKey='table-settings'>
        <TabPane key='table-settings' tab='Settings'>
          <Row gutter={40} className='_mt-24 _pl-16'>
            <Col span={15}>
              <Form
                data-testid='table-settings-form'
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                form={form}
                onFinish={onFinish}
              >
                <PageHeader title='Settings' className='settings-title' />

                <Form.Item name='table_enabled' label='Enable Order at Table'>
                  <Switch
                    defaultChecked={store.settings.table_enabled}
                    disabled={!canManage}
                  />
                </Form.Item>
                <Form.Item name='table_welcome_message' label='Welcome message'>
                  <Input
                    data-testid='welcome-message-field'
                    defaultValue={store.settings.table_welcome_message}
                    placeholder='Order at table'
                    disabled={!canManage}
                  />
                </Form.Item>
                {store.settings.table_enabled && (
                  <Form.Item name='table_url' label='URL'>
                    <Input defaultValue={tableUrl} disabled={!canManage} />
                  </Form.Item>
                )}

                <Divider />

                <PageHeader title='App Setup' className='settings-title' />

                <Form.Item
                  name='table_choices'
                  label='Table Choices'
                  help='This accepts comma-separated values. Please ensure the format is valid (alphanumeric, no spaces). E.g. A1,A2,A3. This option takes precedence over auto-generated table numbers.'
                >
                  <Input
                    type='string'
                    defaultValue={store.settings.table_choices}
                    pattern='[a-zA-Z0-9,]+'
                    required={false}
                    disabled={!canManage}
                  />
                </Form.Item>
                <br />

                <Form.Item
                  name='table_count'
                  label='Table Count'
                  help='Automatically generate table numbers'
                >
                  <Input
                    type='number'
                    defaultValue={store.settings.table_count}
                    min={0}
                    required={true}
                    disabled={!canManage}
                    onFocus={onFocusScrollNumber}
                    onBlur={onBlurScrollNumber}
                  />
                </Form.Item>

                <Form.Item
                  name='table_counter_pickup'
                  label='Allow Counter Pick-up'
                >
                  <Switch
                    defaultChecked={store.settings.table_counter_pickup}
                    disabled={!canManage}
                  />
                </Form.Item>

                <Form.Item
                  name='table_counter_pickup_message'
                  label='Table Pick-up at Counter Message'
                >
                  <Input
                    defaultValue={store.settings.table_counter_pickup_message}
                    disabled={!canManage}
                  />
                </Form.Item>

                <Form.Item className='_mt-32 _mb-40'>
                  <Button
                    title='Save'
                    htmlType='submit'
                    className='_ml-auto _center-vertical'
                    disabled={!canManage}
                  >
                    Save
                  </Button>
                  <UpdateNotice updating={loading} updateError={error} />
                </Form.Item>
              </Form>

              {store.settings.table_enabled && (
                <Row>
                  <Col className='_mb-40'>
                    {tableUrl && <QRCode value={tableUrl} />}
                  </Col>
                </Row>
              )}
            </Col>
            <Col>
              <Divider type='vertical' />
            </Col>
            <Col>
              <PageTooltip title='Related articles from our Help Centre'>
                <a
                  href='https://support.slerp.com/knowledge/order-at-table'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Order at Table Overview
                </a>
              </PageTooltip>
            </Col>
          </Row>
        </TabPane>
      </Tabs>
    </>
  )
}
export default TableSettings
